<template>
    <b-modal v-model="visible" title="SMS Log Audit" @hidden="handleModalHidden" size="lg" scrollable
        :ok-disabled="isSubmitting || !hasSelectedQuestionSet" ok-title="Submit Audit"
        cancel-title="Cancel" @ok="submitAudit">
        <template v-if="smsLog">
            <div class="mb-3">
                <h5>Conversation Information</h5>
                <div class="row">
                    <div class="col-md-6">
                        <strong>External Number:</strong> {{ smsLog.externalNumber }}
                    </div>
                    <div class="col-md-6">
                        <strong>Agent:</strong> {{ smsLog.agent }}
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <strong>Started:</strong> {{ formatDate(smsLog.initiated) }}
                    </div>
                    <div class="col-md-6">
                        <strong>Messages:</strong> {{ smsLog.messageCount }}
                    </div>
                </div>
                <div v-if="smsLog.patientFirstName || smsLog.patientLastName" class="row">
                    <div class="col-md-12">
                        <strong>Patient:</strong> {{ smsLog.patientFirstName }} {{ smsLog.patientLastName }}
                    </div>
                </div>
            </div>

            <hr />

            <!-- Question Set Selection -->
            <div class="mb-3">
                <b-form-group label="Select Audit Form:">
                    <b-select v-model="selectedQuestionSetId" :options="questionSetOptions" />
                </b-form-group>
            </div>

            <!-- Questions -->
            <div v-if="hasSelectedQuestionSet">
                <h5>Audit Questions</h5>
                <TaskQuestions ref="taskQuestions" :questions="selectedQuestionSet.questions" :answers="auditAnswers"
                    :disabled="isSubmitting" :show-errors="showErrors" @question-input="onQuestionInput"
                    :entity-type="EntityType.SMSLog" />
            </div>
        </template>

        <template v-else>
            <div class="text-center">
                <b-spinner />
                <p class="mt-2">Loading conversation information...</p>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { SmsLog } from '@/models/SmsLog';
import { UserTaskQuestionSet, UserTaskAnswer } from '@/models/UserTask';
import { EntityType } from '@/models/EntityType';
import TaskQuestions from '@/components/Tasks/TaskQuestions.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "SmsLogAuditModal",
    components: {
        TaskQuestions
    }
})
export default class SmsLogAuditModal extends Vue {
    EntityType = EntityType;

    @Prop({ required: true }) value!: boolean;
    @Prop({ required: true }) smsLog!: SmsLog | null;

    private questionSets: UserTaskQuestionSet[] = [];
    private selectedQuestionSetId: number | null = null;
    private selectedQuestionSetWithQuestions: UserTaskQuestionSet | null = null;
    private auditAnswers: UserTaskAnswer[] = [];
    private isSubmitting = false;
    private showErrors = false;

    get visible(): boolean {
        return this.value;
    }

    set visible(val: boolean) {
        this.$emit('input', val);
    }

    get questionSetOptions() {
        const defaultOption = { value: null, text: 'Select an audit form...' };
        const options = this.questionSets
            .filter(qs => qs.active)
            .map(qs => ({ value: qs.id, text: qs.name }));
        return [defaultOption, ...options];
    }

    get hasSelectedQuestionSet(): boolean {
        return this.selectedQuestionSetId !== null;
    }

    get selectedQuestionSet(): UserTaskQuestionSet | null {
        return this.selectedQuestionSetWithQuestions;
    }

    @Watch('selectedQuestionSetId')
    async onQuestionSetChange(newVal: number | null) {
        if (newVal) {
            await this.loadQuestionSetWithQuestions(newVal);
            if (this.selectedQuestionSetWithQuestions) {
                this.initializeAnswers();
            }
        } else {
            this.selectedQuestionSetWithQuestions = null;
            this.auditAnswers = [];
        }
    }

    async loadQuestionSetWithQuestions(questionSetId: number) {
        try {
            const response = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${questionSetId}`);
            this.selectedQuestionSetWithQuestions = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Failed to load question set with questions:', err);
        }
    }

    @Watch('visible')
    async onVisibleChange(newVal: boolean) {
        if (newVal) {
            await this.loadQuestionSets();
            this.resetForm();
        }
    }

    async loadQuestionSets() {
        try {
            const response = await this.$http.get<UserTaskQuestionSet[]>(`/task/question-sets/${EntityType.SMSLog}`);
            this.questionSets = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Failed to load question sets:', err);
        }
    }

    resetForm() {
        this.selectedQuestionSetId = null;
        this.selectedQuestionSetWithQuestions = null;
        this.auditAnswers = [];
        this.isSubmitting = false;
        this.showErrors = false;
    }

    initializeAnswers() {
        if (!this.selectedQuestionSet || !this.smsLog || !this.smsLog.id) return;

        this.auditAnswers = this.selectedQuestionSet.questions.map(q => ({
            id: 0,
            taskId: this.smsLog!.id!,
            taskQuestionId: q.id,
            answer: '',
            entityType: EntityType.SMSLog
        }));
    }

    onQuestionInput() {
        // TaskQuestions component manages answers internally
    }

    formatDate(date: Date | string | undefined): string {
        if (!date) return '';
        return moment.utc(date).local().format("L LT");
    }

    async submitAudit(bvModalEvent: { preventDefault: () => void }) {
        // Prevent BootstrapVue from auto-closing the modal; we close manually only on success.
        bvModalEvent.preventDefault();

        if (!this.smsLog || !this.smsLog.id || !this.selectedQuestionSet || !this.selectedQuestionSetId) {
            this.$notification(NotificationOptions.error("Missing required audit information"));
            return;
        }

        this.showErrors = false;

        const answeredQuestions = this.auditAnswers.filter(answer => answer.answer && answer.answer.trim());

        const requiredQuestions = this.selectedQuestionSet.questions.filter(q => q.required);
        const answeredRequiredQuestions = requiredQuestions.filter(req =>
            answeredQuestions.some(ans => ans.taskQuestionId === req.id)
        );

        if (answeredRequiredQuestions.length < requiredQuestions.length) {
            this.showErrors = true;
            this.$notification(NotificationOptions.error("Please answer all required questions"));
            return;
        }

        if (answeredQuestions.length === 0) {
            this.$notification(NotificationOptions.error("Please answer at least one question"));
            return;
        }

        this.isSubmitting = true;

        try {
            const response = await this.$http.post<{ success: boolean; auditCompletedBy: string; auditCompletedAt: string }>(
                '/SmsLog/audit-answers', {
                smsLogId: this.smsLog.id,
                questionSetId: this.selectedQuestionSetId,
                answers: answeredQuestions
            });

            this.$notification(NotificationOptions.success("Audit submitted successfully"));
            this.$emit('audit-submitted', {
                smsLogId: this.smsLog.id,
                auditCompletedBy: response.data.auditCompletedBy,
                auditCompletedAt: response.data.auditCompletedAt,
            });
            this.visible = false;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Failed to submit audit:', err);
        } finally {
            this.isSubmitting = false;
        }
    }

    handleModalHidden() {
        this.resetForm();
    }
}
</script>

<style scoped>
.sms-info {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.25rem;
    margin-bottom: 1rem;
}
</style>
